// Calculator UI copy that SCERS can revise from the database alongside the
// actuarial tables: field-level help text and the beneficiary-relationship
// options. Served by GET /api/retirement-calculator/reference-data
// (retirement_calculator.help_topic / help_topic_paragraph /
// beneficiary_relationship); these bundled copies are the fallback.

export interface HelpContent {
  title: string;
  body: readonly string[];
}

export interface BeneficiaryRelationshipOption {
  code: string;
  label: string;
}

// Help copy for the field-level "?" buttons, lifted verbatim from the
// original calculator's ack modals.
export const HELP_CONTENT: Record<string, HelpContent> = {
  service: {
    title: 'Service Credit at Retirement Date',
    body: [
      'Enter the total service credit you expect to have in this Plan / Tier as of your selected retirement date.',
      'If your retirement date is in the future, include the service credit you expect to earn through that date. If your retirement date is today, enter your current service credit.',
    ],
  },
  comp: {
    title: 'Estimated Final Monthly Compensation',
    body: [
      'Final compensation is not your take-home pay or simply your current monthly salary. It is the average of your SCERS-reportable compensation over the consecutive-month period that applies to your Plan / Tier.',
      'For example, if your applicable final-compensation period is 36 months, use the average of your SCERS-reportable compensation for 36 consecutive months. Enter the best estimate available for the retirement scenario you are testing.',
    ],
  },
  sickHours: {
    title: 'Unused Sick Leave Hours',
    body: [
      'If you expect to have unused sick leave when you retire, enter the estimated number of hours here. The calculator converts those hours to additional service credit for purposes of estimating your retirement allowance.',
      'In this calculator, sick-leave service is applied to your current or most recent Plan / Tier. It is not counted toward the minimum regular-service requirement used to determine retirement eligibility.',
    ],
  },
  priorMembership: {
    title: 'Service Credit in Another Plan / Tier',
    body: [
      'Select Yes if you have earned SCERS service credit under more than one Plan / Tier.',
      'For example, you may have changed membership tiers during your career or previously earned service under a different SCERS-covered classification.',
      'If you select Yes, you can add each prior Plan / Tier and the service credit earned under it. The calculator will include those memberships in your combined retirement estimate so you do not have to calculate and add them separately.',
    ],
  },
  benRelationship: {
    title: 'Beneficiary Relationship',
    body: [
      'Select Spouse / Domestic Partner when the beneficiary is your spouse or registered domestic partner.',
      'Select Insurable Interest for another person whose relationship to you may qualify for a survivor continuance. Additional requirements and federal limits may apply; selecting this option will display more information.',
    ],
  },
};

// The engine only understands these codes ('insurable' applies the federal
// nonspouse survivor limit; anything else is treated as a spouse), so the
// database can relabel or reorder them but not add new ones.
export const BENEFICIARY_RELATIONSHIP_OPTIONS: readonly BeneficiaryRelationshipOption[] = [
  { code: 'spouse', label: 'Spouse / Domestic Partner' },
  { code: 'insurable', label: 'Insurable Interest' },
];
